import Navbar from "lib/components/Navbar";
import WithTryLoggingIn from "lib/hoc/withTryLoggingIn";
import Footer from "lib/components/Footer";
import InfoStrip from "lib/components/atomic/InfoStrip";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import {
  getOnBoardingStatus,
  getUser,
  isLoggedIn,
} from "redux/reducers/userSlice";
import React, { useCallback, useState } from "react";
import myaxios from "lib/myAxios";
import { resendVerificationMail } from "lib/common/endpoints/endUser";
import { message as _message } from "antd";
import useSessionStorage from "lib/hooks/useSessionStorage";
import DashboardLayoutInner from "components/DashboardLayout";

interface props {
  children: React.ReactNode;
}

const DefaultLayout = WithTryLoggingIn(({ children }: props) => {
  const OnboardStatus = useAppSelector(getOnBoardingStatus);
  const user = useAppSelector(getUser);
  const loggedIn = useAppSelector(isLoggedIn);
  const [loading, setLoading] = useState(false);
  const { storedValue } = useSessionStorage("hidePopup");

  const resendMail = useCallback(() => {
    setLoading(true);
    _message.loading("sending verification mail");
    myaxios
      .put(resendVerificationMail)
      .then(({ data: { message, success } }) => {
        if (success && message) _message.success(message);
        else _message.error(message);
      })
      .catch(({ response }) => {
        const message = response?.data?.message;
        _message.error(message);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loggedIn) {
    return (
      <DashboardLayoutInner>
        {loggedIn && !OnboardStatus?.mobileVerified && (
          <InfoStrip closeButton variant="WARNING">
            You have not verified your mobile yet.{" "}
            <Link href="/legacyuser/mobileupdate">
              <span className="text-primary font-semibold cursor-pointer">
                Click here
              </span>
            </Link>{" "}
            to complete the process.
          </InfoStrip>
        )}
        {loggedIn &&
          OnboardStatus?.mobileVerified &&
          user?.userData?.email &&
          !user?.userData?.emailVerified && (
            <InfoStrip closeButton variant="WARNING">
              <div className="flex gap-3">
                <p>
                  We have sent email at {user.userData.email}. Please, verify
                  your email to use it. or{" "}
                  <span
                    className="text-primary font-semibold underline cursor-pointer"
                    onClick={resendMail}
                  >
                    resend verification mail
                  </span>
                </p>
              </div>
            </InfoStrip>
          )}
        {children}
      </DashboardLayoutInner>
    );
  }

  return (
    <>
      <Navbar />
      <main className="overflow-x-hidden transition-colors dark:bg-gray-900">{children}</main>
      <Footer />
    </>
  );
});

export const defaultLayout = (page: React.ReactNode): React.ReactNode => (
  <DefaultLayout>{page}</DefaultLayout>
);

const LandingLayout = WithTryLoggingIn(({ children }: props) => {
  return (
    <>
      <Navbar />
      <main className="overflow-x-hidden transition-colors dark:bg-gray-900">{children}</main>
      <Footer />
    </>
  );
});

export const landingLayout = (page: React.ReactNode): React.ReactNode => (
  <LandingLayout>{page}</LandingLayout>
);

export default DefaultLayout;

// This file exists to be imported as a component/layout by other pages, not
// to be its own public route. Next.js Pages Router treats any file here with
// a default export as a real URL though, so without this it was silently
// indexable at its own path with broken/duplicate content. This forces a 404
// for that accidental route instead.
export async function getStaticProps() {
  return { notFound: true };
}
